/**
 * AppHeader — top bar: sidebar toggle + brand (left), global command launcher
 * (center), user + logout (right). Notion/workspace soft-stone aesthetic.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Boxes, Search, LogOut, PanelLeft, Bell, AlertTriangle } from 'lucide-react';
import { approvalsApi, routinesApi } from '../../services/routinesApi';

const AppHeader = ({ user, logout, onOpenCommand, onToggleSidebar }) => {
  const navigate = useNavigate();
  const [awaiting, setAwaiting] = useState(0);
  const [stalled, setStalled] = useState(0);

  useEffect(() => {
    let active = true;
    const load = () => {
      approvalsApi.count()
        .then((r) => { if (active) setAwaiting(r.data?.awaiting || 0); }).catch(() => {});
      routinesApi.notificationsCount()
        .then((r) => { if (active) setStalled(r.data?.count || 0); }).catch(() => {});
    };
    load();
    const t = setInterval(load, 300000);
    return () => { active = false; clearInterval(t); };
  }, []);

  const initials = (user?.full_name || 'User')
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <header
      className="bg-white/80 backdrop-blur-md border-b border-gray-200 h-16 flex items-center sticky top-0 z-50"
      data-testid="app-header"
    >
      <div className="flex items-center justify-between px-4 w-full gap-4">
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={onToggleSidebar}
            data-testid="sidebar-toggle"
            className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            title="Toggle sidebar"
          >
            <PanelLeft className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2.5 group"
            data-testid="header-brand"
          >
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center group-hover:opacity-90 transition-opacity">
              <Boxes className="w-5 h-5 text-white" strokeWidth={1.75} />
            </div>
            <span className="font-heading text-lg font-semibold text-gray-900 tracking-tight hidden sm:inline">
              One ERP
            </span>
          </button>
        </div>

        {/* Command launcher */}
        <button
          onClick={onOpenCommand}
          data-testid="command-palette-trigger"
          className="flex-1 max-w-md flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200/70 border border-gray-200 rounded-lg text-gray-500 transition-colors"
        >
          <Search className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-sm">Search or jump to...</span>
          <kbd className="ml-auto text-[11px] font-medium text-gray-400 bg-white border border-gray-200 rounded px-1.5 py-0.5 hidden sm:inline">
            ⌘K
          </kbd>
        </button>

        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={() => navigate('/routines')}
            data-testid="header-stalled-bell"
            className="relative p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            title="Stalled routines needing attention"
          >
            <AlertTriangle className="w-5 h-5" strokeWidth={1.5} />
            {stalled > 0 && (
              <span data-testid="header-stalled-badge"
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-semibold">
                {stalled > 99 ? '99+' : stalled}
              </span>
            )}
          </button>
          <button
            onClick={() => navigate('/approvals')}
            data-testid="header-approvals-bell"
            className="relative p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            title="My Approvals"
          >
            <Bell className="w-5 h-5" strokeWidth={1.5} />
            {awaiting > 0 && (
              <span data-testid="header-approvals-badge"
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-orange-600 text-white text-[10px] font-semibold">
                {awaiting > 99 ? '99+' : awaiting}
              </span>
            )}
          </button>
          <div className="text-right hidden md:block">
            <p className="text-sm font-medium text-gray-800 leading-tight">{user?.full_name || 'User'}</p>
            <p className="text-xs text-gray-400 capitalize">{user?.role || 'admin'}</p>
          </div>
          <div className="w-9 h-9 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-xs font-semibold text-gray-600">
            {initials}
          </div>
          <button
            onClick={() => {
              logout();
              navigate('/login');
            }}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Logout"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
